import { VisualRandom } from '../core/VisualRandom.js';
import {
  DEFAULT_PALETTE_ID,
  paletteColor,
  resolvePalette,
  type Rgb,
  type VisualPalette,
} from '../palette/VisualPalette.js';
import {
  GAMEPLAY_WAVE_STYLES,
  gameplayWaveSeed,
  type GameplayWaveStyle,
} from './GameplayWave.js';

/**
 * Decides which style each gameplay wave is drawn in.
 *
 * The counterpart to Phase 6B's `WaveDirector`, and deliberately **not** the
 * same object. The artwork director chooses what the background emits; this one
 * chooses how the thing the player taps presents itself. They answer to
 * different settings, they are seeded separately, and either can be changed
 * without disturbing the other — which is the whole point of §23: a watercolour
 * background with code waves coming at you has to be a combination somebody can
 * actually ask for.
 *
 * ## Determinism
 *
 * Every decision is a pure function of the wave's event index and the recipe's
 * seed, taken through `VisualRandom`. Never `Math.random`, and never a counter
 * that depends on how many waves happened to be alive — a wave's style has to
 * be the same on a replay as it was on the run, and a replay does not
 * necessarily reach a given wave with the same history behind it.
 *
 * SHUFFLE is the one mode that needs memory, and it earns it: a bag that
 * refills only when empty is what "no repeats until every style has had a turn"
 * means. Its bag is rebuilt from the seed at reset, so a replay walks the same
 * sequence.
 */

export const GAMEPLAY_WAVE_MODES = [
  'FIXED',
  'RANDOM',
  'SHUFFLE',
  'WEIGHTED_RANDOM',
  'AUDIO_REACTIVE',
] as const;
export type GameplayWaveMode = (typeof GAMEPLAY_WAVE_MODES)[number];

export function isGameplayWaveMode(value: string): value is GameplayWaveMode {
  return (GAMEPLAY_WAVE_MODES as readonly string[]).includes(value);
}

/** Band energies at a moment, for AUDIO_REACTIVE. */
export interface GameplayAudioSample {
  readonly bass: number;
  readonly mid: number;
  readonly treble: number;
  /** 0..1 onset strength at this instant. */
  readonly onset: number;
}

export interface GameplayWaveSettings {
  readonly mode: GameplayWaveMode;
  /** Used by FIXED, and as the fallback whenever a mode cannot decide. */
  readonly defaultStyle: GameplayWaveStyle;
  /** Relative weights for WEIGHTED_RANDOM. Absent styles do not appear. */
  readonly styles: Readonly<Partial<Record<GameplayWaveStyle, number>>>;
  /**
   * Which colours the pigment materials use.
   *
   * The same palette ids the background uses — `PRIMARY`, `NEON`, `WARM`,
   * `COOL`, `RANDOM_PIGMENT` — resolved through the same
   * `resolvePalette`. There is deliberately no gameplay-specific palette
   * system: colour definitions have one home, and a second table would drift
   * from it the first time somebody adjusted a red.
   */
  readonly palette: string;
  readonly seed: number;
}

/**
 * What a recipe that says nothing means.
 *
 * MANDALA, fixed. An older recipe predates every other style, and a save that
 * silently started producing smoke because the format grew a field would be a
 * worse outcome than one that looks exactly as it did before.
 */
export const DEFAULT_GAMEPLAY_WAVE_SETTINGS: GameplayWaveSettings = {
  mode: 'FIXED',
  defaultStyle: 'MANDALA',
  styles: {},
  palette: DEFAULT_PALETTE_ID,
  seed: 0,
};

/** Styles the audio rules can reach for, grouped by what they suit. */
const AUDIO_GROUPS = {
  heavy: ['SMOKE', 'INK_PAINT'] as const,
  bright: ['CODE', 'MATH', 'RIPPLE'] as const,
  quiet: ['SUMI', 'WATERCOLOR'] as const,
  onset: ['RIPPLE'] as const,
};

export class GameplayWaveDirector {
  private settings: GameplayWaveSettings = DEFAULT_GAMEPLAY_WAVE_SETTINGS;
  /** SHUFFLE's remaining styles for the current cycle. */
  private bag: GameplayWaveStyle[] = [];
  /** Which style each wave was given, so a wave never changes mid-flight. */
  private readonly assigned = new Map<number, GameplayWaveStyle>();
  private bagRandom = new VisualRandom(0);
  /**
   * The palette the pigment styles draw from.
   *
   * Resolved once when the settings change rather than per wave: a generated
   * palette rebuilt for every wave would be a different palette every time, and
   * the whole set would be unreproducible.
   */
  private palette: VisualPalette = resolvePalette(DEFAULT_PALETTE_ID, 0);
  /** Which pigment each wave was given, so a wave never changes colour. */
  private readonly pigments = new Map<number, Rgb>();

  constructor(settings: GameplayWaveSettings = DEFAULT_GAMEPLAY_WAVE_SETTINGS) {
    this.apply(settings);
  }

  get current(): GameplayWaveSettings {
    return this.settings;
  }

  apply(settings: GameplayWaveSettings): void {
    this.settings = settings;
    this.palette = resolvePalette(settings.palette, settings.seed);
    this.reset();
  }

  /** Forgets every assignment. Called when a song or a recipe changes. */
  reset(): void {
    this.assigned.clear();
    this.pigments.clear();
    this.bagRandom = new VisualRandom(this.settings.seed ^ 0x5eed5eed);
    this.bag = [];
  }

  /**
   * The style for one wave.
   *
   * Memoised per event index, and that is not an optimisation. A wave is asked
   * about every frame it is alive; a mode that answered from a running random
   * stream would give it a different style each frame and the wave would strobe
   * through the whole set on its way in.
   */
  styleFor(eventIndex: number, audio?: GameplayAudioSample): GameplayWaveStyle {
    const existing = this.assigned.get(eventIndex);
    if (existing !== undefined) return existing;
    const chosen = this.choose(eventIndex, audio);
    this.assigned.set(eventIndex, chosen);
    return chosen;
  }

  private choose(eventIndex: number, audio?: GameplayAudioSample): GameplayWaveStyle {
    const settings = this.settings;
    switch (settings.mode) {
      case 'FIXED':
        return settings.defaultStyle;

      case 'RANDOM': {
        // Seeded from the wave itself, so the answer does not depend on how
        // many waves came before it — a replay that starts mid-song still gets
        // the same styles as one that played from the top.
        const random = this.randomFor(eventIndex);
        const index = Math.floor(random.next() * GAMEPLAY_WAVE_STYLES.length);
        return GAMEPLAY_WAVE_STYLES[Math.min(index, GAMEPLAY_WAVE_STYLES.length - 1)] as GameplayWaveStyle;
      }

      case 'SHUFFLE': {
        if (this.bag.length === 0) this.refillBag();
        const taken = this.bag.pop();
        return taken ?? settings.defaultStyle;
      }

      case 'WEIGHTED_RANDOM':
        return this.weighted(eventIndex);

      case 'AUDIO_REACTIVE':
        return this.fromAudio(eventIndex, audio);

      default:
        return settings.defaultStyle;
    }
  }

  /** A stream that belongs to one wave alone. */
  private randomFor(eventIndex: number): VisualRandom {
    return new VisualRandom((this.settings.seed ^ gameplayWaveSeed(eventIndex)) >>> 0);
  }

  /**
   * Refills SHUFFLE's bag with every style, in a seeded order.
   *
   * Fisher–Yates over the full list, so one cycle visits each style exactly
   * once. The only repeat possible is across a boundary — the last of one cycle
   * and the first of the next — which is the accepted cost of a bag and is far
   * less clumpy than independent draws.
   */
  private refillBag(): void {
    const styles = [...GAMEPLAY_WAVE_STYLES];
    for (let index = styles.length - 1; index > 0; index -= 1) {
      const swap = Math.floor(this.bagRandom.next() * (index + 1));
      const a = styles[index] as GameplayWaveStyle;
      const b = styles[swap] as GameplayWaveStyle;
      styles[index] = b;
      styles[swap] = a;
    }
    this.bag = styles;
  }

  private weighted(eventIndex: number): GameplayWaveStyle {
    const entries: { style: GameplayWaveStyle; weight: number }[] = [];
    let total = 0;
    for (const style of GAMEPLAY_WAVE_STYLES) {
      const weight = this.settings.styles[style] ?? 0;
      if (weight > 0) {
        entries.push({ style, weight });
        total += weight;
      }
    }
    // A weight table that names nothing is a configuration error, not a reason
    // to invent variety: fall back to what the recipe said it wanted.
    if (total <= 0) return this.settings.defaultStyle;

    let ticket = this.randomFor(eventIndex).next() * total;
    for (const entry of entries) {
      ticket -= entry.weight;
      if (ticket <= 0) return entry.style;
    }
    return entries[entries.length - 1]?.style ?? this.settings.defaultStyle;
  }

  /**
   * Picks from the music, by rule rather than by classifier.
   *
   * Four bands and a threshold each. This is not music analysis and is not
   * trying to be — the brief asks for something legible, and a rule a reader can
   * check against what they hear is worth more here than a model they cannot.
   *
   * The choice *within* a group is still seeded per wave, so two waves that fall
   * in the same band do not have to be the same style.
   */
  private fromAudio(eventIndex: number, audio?: GameplayAudioSample): GameplayWaveStyle {
    if (audio === undefined) return this.settings.defaultStyle;
    const random = this.randomFor(eventIndex);
    const pick = (group: readonly GameplayWaveStyle[]): GameplayWaveStyle =>
      group[Math.min(Math.floor(random.next() * group.length), group.length - 1)] as GameplayWaveStyle;

    // Onset first: a transient is the most specific thing that can be true of a
    // moment, so it should not be outvoted by a band that is merely loud.
    if (audio.onset > 0.6) return pick(AUDIO_GROUPS.onset);

    const loudest = Math.max(audio.bass, audio.mid, audio.treble);
    if (loudest < 0.25) return pick(AUDIO_GROUPS.quiet);
    if (audio.bass >= audio.treble && audio.bass >= audio.mid) return pick(AUDIO_GROUPS.heavy);
    if (audio.treble >= audio.mid) return pick(AUDIO_GROUPS.bright);
    return pick(AUDIO_GROUPS.bright);
  }

  /**
   * The colour one wave paints with.
   *
   * ## Why this is a separate stream from the style
   *
   * §7 asks that choosing materials at random does not disturb the colours, and
   * the only way to guarantee that is to derive them from different numbers. The
   * style comes from `seed ^ gameplayWaveSeed(index)`; the pigment comes from
   * the wave's index through its own offset. So switching the mode from FIXED to
   * SHUFFLE changes which materials appear and leaves the colour sequence
   * exactly where it was — and re-rolling the palette does not reshuffle the
   * materials.
   *
   * Memoised for the same reason the style is: a wave is asked every frame it is
   * alive, and a colour recomputed each time would strobe.
   */
  pigmentFor(eventIndex: number): Rgb {
    const existing = this.pigments.get(eventIndex);
    if (existing !== undefined) return existing;
    // A stepped cursor rather than a draw, so consecutive waves are different
    // colours instead of occasionally three reds in a row. The step is coprime
    // with a five-entry palette, so the whole set is visited.
    // The offset is hashed, not shifted. `seed >>> 3` discarded the low bits,
    // so every small seed collapsed to the same starting colour — seeds 1 and 2
    // produced identical artwork.
    const cursor = eventIndex * 2 + (gameplayWaveSeed(this.settings.seed) % 977);
    const colour = paletteColor(this.palette, cursor);
    this.pigments.set(eventIndex, colour);
    return colour;
  }

  /** The palette in use, for diagnostics and the panel's swatches. */
  get activePalette(): VisualPalette {
    return this.palette;
  }

  /** Which styles have been handed out so far, for diagnostics and tests. */
  assignments(): ReadonlyMap<number, GameplayWaveStyle> {
    return this.assigned;
  }
}
